import { create } from "zustand";
import { persist } from "zustand/middleware";
import { apiClient } from "../lib/api-client";
import { clearCurrentKey, deriveKey, deriveTenantKey, generateVaultKey, getStoredKeyForTenant, storeKey, clearKey, unwrapVaultKey, wrapVaultKey, WRAPPED_KEY_MODES, wrapIdentifierFor } from "../lib/crypto";

const fixedTenantName = (environmentType) => environmentType === "business" ? "Empresa" : "Pessoal";

const sortFixedTenants = (tenants = []) => [...tenants].sort((a, b) => {
  const order = { personal: 0, business: 1 };
  return (order[a.environment_type] ?? 9) - (order[b.environment_type] ?? 9);
});

const uniqueTenants = (tenants = []) => {
  const byId = new Map();
  tenants.filter(Boolean).forEach((tenant) => byId.set(tenant.id, tenant));
  return sortFixedTenants([...byId.values()]);
};

const needsLoginRefreshForUnlock = (tenant) => [...WRAPPED_KEY_MODES, "legacy_user"].includes(tenant?.crypto_mode);

const pickUnlockedTenant = (previousTenant, freshActiveTenant, tenants = []) => {
  const candidates = uniqueTenants([freshActiveTenant, ...tenants]);
  if (!candidates.length) return freshActiveTenant || previousTenant || null;

  return (
    candidates.find((tenant) => tenant.id === previousTenant?.id) ||
    candidates.find((tenant) => tenant.environment_type === previousTenant?.environment_type) ||
    candidates.find((tenant) => tenant.id === freshActiveTenant?.id) ||
    candidates.find((tenant) => tenant.environment_type === "personal") ||
    candidates[0]
  );
};

const tenantHeaders = (tenant) => (
  tenant?.id ? { headers: { "X-Tenant-ID": tenant.id } } : undefined
);

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      activeTenant: null,
      tenants: [],
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Login
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiClient.post("/auth/login", {
            email,
            password,
          });

          const { user, active_tenant } = response.data;
          let unlocked = false;
          let activeTenant = active_tenant;

          const cryptoKey = await get().unlockTenantKey(activeTenant, password, user);
          if (cryptoKey) {
            storeKey(cryptoKey, activeTenant.id);
            unlocked = true;
          }

          set({
            user,
            activeTenant,
            isAuthenticated: true,
            isLoading: false,
          });

          const tenants = await get().ensureFixedTenants(password);
          activeTenant = tenants.find((tenant) => tenant.environment_type === "personal") || activeTenant;
          const personalKey = getStoredKeyForTenant(activeTenant.id) || cryptoKey;
          if (personalKey) storeKey(personalKey, activeTenant.id);
          set({ activeTenant });

          return { success: true, unlocked };
        } catch (error) {
          const message = error.response?.data?.detail || error.message || "Erro ao fazer login";
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      // Registro
      register: async (name, email, password, contractData = {}) => {
        set({ isLoading: true, error: null });
        try {
          // Gerado no cliente porque o wrap da vault key precisa de um identificador
          // estável antes da conta existir no servidor; vira o id definitivo do usuário.
          const userId = window.crypto.randomUUID();
          const cryptoKey = await generateVaultKey();
          const wrappedKey = await wrapVaultKey(cryptoKey, password, userId);
          const response = await apiClient.post("/auth/register", {
            name,
            email,
            password,
            user_id: userId,
            wrapped_key: wrappedKey,
            ...contractData,
          });

          const { user, active_tenant } = response.data;
          storeKey(cryptoKey, active_tenant.id);

          set({
            user,
            activeTenant: active_tenant,
            isAuthenticated: true,
            isLoading: false,
          });

          const tenants = await get().ensureFixedTenants(password);
          const personalTenant = tenants.find((tenant) => tenant.environment_type === "personal") || active_tenant;
          storeKey(cryptoKey, personalTenant.id);
          set({ activeTenant: personalTenant });

          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || "Erro ao registrar";
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      requestPasswordRecovery: async (email) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiClient.post("/auth/password-recovery/request", { email });
          set({ isLoading: false });
          return { success: true, message: response.data.message };
        } catch (error) {
          const message = error.response?.data?.detail || "Erro ao solicitar recuperação";
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      validatePasswordRecoveryToken: async (token) => {
        try {
          const response = await apiClient.get("/auth/password-recovery/validate", {
            params: { token },
          });
          return { success: true, data: response.data };
        } catch (error) {
          const message = error.response?.data?.detail || "Link de recuperação inválido";
          return { success: false, error: message };
        }
      },

      completePasswordRecovery: async (token, password, wrappedKey) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiClient.post("/auth/password-recovery/complete", {
            token,
            password,
            wrapped_key: wrappedKey,
          });
          set({ isLoading: false });
          return { success: true, message: response.data.message };
        } catch (error) {
          const message = error.response?.data?.detail || "Erro ao redefinir senha";
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      // Logout
      logout: async () => {
        try {
          await apiClient.post("/auth/logout");
        } catch {
          // Best-effort: a sessão local é limpa de qualquer forma.
        }
        clearKey();
        set({
          user: null,
          activeTenant: null,
          tenants: [],
          isAuthenticated: false,
          error: null,
        });
      },

      setActiveTenant: (activeTenant) => {
        set({ activeTenant });
      },

      getAuthHeaders: () => {
        const { activeTenant } = get();
        return tenantHeaders(activeTenant) || {};
      },

      unlockTenantKey: async (tenant, password, userOverride = null) => {
        const user = userOverride || get().user;
        if (!tenant || !user) return null;

        if (WRAPPED_KEY_MODES.includes(tenant.crypto_mode)) {
          if (!tenant.wrapped_key) {
            throw new Error("Acesso ao cofre pendente. Peça para um admin alterar sua senha para liberar o cofre.");
          }
          return unwrapVaultKey(tenant.wrapped_key, password, wrapIdentifierFor(tenant.crypto_mode, user));
        }

        if (tenant.crypto_mode === "legacy_user") {
          return deriveKey(password, user.email);
        }

        return deriveTenantKey(password, tenant.id);
      },

      fetchTenants: async () => {
        const { user, activeTenant } = get();
        if (!user) return [];
        const response = await apiClient.get("/tenants", tenantHeaders(activeTenant));
        const tenants = sortFixedTenants(response.data);
        set({ tenants });
        return tenants;
      },

      ensureFixedTenants: async (password = null) => {
        const { user, activeTenant } = get();
        if (!user) return [];

        let tenants = await get().fetchTenants();
        const personal = tenants.find((tenant) => tenant.environment_type === "personal");
        if (personal && personal.name !== fixedTenantName("personal")) {
          personal.name = fixedTenantName("personal");
        }

        if (!tenants.some((tenant) => tenant.environment_type === "business") && password) {
          const businessKey = await generateVaultKey();
          // Tenant novo — o backend sempre cria com crypto_mode "wrapped_key_v2".
          const wrappedKey = await wrapVaultKey(businessKey, password, user.id);
          const response = await apiClient.post(
            "/tenants/fixed",
            { environment_type: "business", wrapped_key: wrappedKey },
            tenantHeaders(activeTenant)
          );
          storeKey(businessKey, response.data.id);
          tenants = sortFixedTenants([...tenants, response.data]);
        }

        set({ tenants });
        return tenants;
      },

      switchTenant: async (tenantOrEnvironmentType) => {
        const { tenants } = get();
        const target = typeof tenantOrEnvironmentType === "string"
          ? tenants.find((tenant) => tenant.environment_type === tenantOrEnvironmentType || tenant.id === tenantOrEnvironmentType)
          : tenantOrEnvironmentType;

        if (!target) return { success: false, needsUnlock: false };

        const cachedKey = getStoredKeyForTenant(target.id);
        set({ activeTenant: target });

        if (cachedKey) {
          storeKey(cachedKey, target.id);
          return { success: true, needsUnlock: false };
        }

        clearCurrentKey();
        return { success: false, needsUnlock: true };
      },

      // Re-deriva a chave do cofre (necessário após reload da página)
      rederiveKey: async (password) => {
        let { user, activeTenant } = get();
        if (!user) return false;

        try {
          if (!activeTenant) {
            const response = await apiClient.get("/auth/me", tenantHeaders(activeTenant));
            user = response.data.user;
            activeTenant = response.data.active_tenant;
            set({ user, activeTenant });
          }

          if (!activeTenant) return false;

          let cryptoKey;
          let unlockedTenant = activeTenant;
          const shouldRefreshWithLogin = needsLoginRefreshForUnlock(activeTenant);

          if (shouldRefreshWithLogin) {
            const previousTenant = activeTenant;
            const response = await apiClient.post("/auth/login", {
              email: user.email,
              password,
            });

            user = response.data.user;
            activeTenant = response.data.active_tenant;
            set({
              user,
              activeTenant,
              isAuthenticated: true,
            });
            const tenants = await get().ensureFixedTenants(password);
            unlockedTenant = pickUnlockedTenant(previousTenant, activeTenant, tenants);
            cryptoKey = await get().unlockTenantKey(unlockedTenant, password, user);
          } else {
            cryptoKey = await deriveTenantKey(password, activeTenant.id);
          }

          if (!cryptoKey || !unlockedTenant?.id) return false;

          storeKey(cryptoKey, unlockedTenant.id);
          set({ activeTenant: unlockedTenant });

          if (!shouldRefreshWithLogin) {
            await get().ensureFixedTenants(password);
          }
          return true;
        } catch {
          return false;
        }
      },

      // Limpa erros
      clearError: () => set({ error: null }),
    }),
    {
      name: "cofre-auth",
      partialize: (state) => ({
        user: state.user,
        activeTenant: state.activeTenant,
        tenants: state.tenants,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
